/* global BigInt */
import { hasStockOperationPermission } from "../../../utils/purchasingStockPermissions";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "../../../api/axiosInstance";
import useIsMobile from "../../../hooks/useIsMobile";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import { stockTransferText } from "./stockTransferMessages";
import { inventoryBasis, exactInput, historicalExact, quantityFromExact, displayQuantity, stockInUnit, formatExactQuantity } from "../../utils/inventoryUnitQuantity";
import { getUnitName } from "../../utils/unitNames";
import { getLocationName } from "../../utils/locationNames";
import StockOperationsDialog from "../popups/StockOperationsDialog";
import StockApprovalHistory, { StockReturnNotice } from "../StockApprovalHistory";
import { useStockApprovalDecision } from "../popups/StockApprovalDecisionDialog";
import { useDocumentDialog } from "../popups/PurchasingDocumentDialog";
import { FiPlus, FiSearch, FiX, FiArrowRight, FiCheck } from "react-icons/fi";
import "./StockTransfersWorkspace.css";
import {
  formatStockOpsCurrency,
  formatStockOpsDate,
  formatStockTransferQuantity,
  truncateStockOpsText,
} from "../stockOperationsUi";

const TRANSFER_STATUS_META = {
  pending_approval: { label: "Pending Approval", tone: "confirmed" },
  returned: { label: "Returned", tone: "partial" },
  approved: { label: "Approved", tone: "approved" },
  completed: { label: "Completed", tone: "dispatched" },
  rejected: { label: "Rejected", tone: "cancelled" },
  cancelled: { label: "Cancelled", tone: "cancelled" },
};

const TRANSFER_CREATION_PLANS = {
  pending_approval: { actions: ["request"], message: "The request will be created. Approval and dispatch will remain pending; no stock will move." },
  approved: { actions: ["request", "approve"], message: "The request will be created and approved automatically. Dispatch will remain pending; no stock will move." },
  completed: { actions: ["request", "approve", "dispatch"], message: "The request will be created, approved and dispatched automatically. The selected quantities will move from the source to the destination now." },
};

const TRANSFER_ITEM_GROUPS = [
  { key: "family", filter: "Filter by family", select: "Select a family" },
  { key: "subfamily", filter: "Filter by subfamily", select: "Select a subfamily" },
  { key: "category", filter: "Filter by category", select: "Select a category" },
  { key: "subcategory", filter: "Filter by subcategory", select: "Select a subcategory" },
];

function itemGroup(item, level) {
  const name = item[`${level}_name`];
  if (!name) return null;
  const path = TRANSFER_ITEM_GROUPS.slice(0, TRANSFER_ITEM_GROUPS.findIndex((group) => group.key === level) + 1)
    .map((group) => item[`${group.key}_name`] || "");
  // The transfer context currently exposes names. Include ancestors so equal
  // category names in different branches remain separate groups.
  return {
    key: item[`${level}_id`] != null ? `${level}:${item[`${level}_id`]}` : JSON.stringify(path),
    name, path: path.filter(Boolean).join(" / "), parent: path.slice(0, -1).filter(Boolean).join(" / "),
  };
}

function readCurrentUser() {
  try {
    return JSON.parse(localStorage.getItem("palantir_user")) || {};
  } catch (error) {
    return {};
  }
}

const TRANSFER_ROLE_ACTIONS = {
  request: "create", edit: "create", resubmit: "create", approve: "approve",
  reject: "approve", return: "approve", dispatch: "dispatch", cancel: "cancel",
};
function hasTransferRolePermission(user, action) {
  return hasStockOperationPermission(user, "transfers", action ? TRANSFER_ROLE_ACTIONS[action] : "view");
}

function positiveSessionId(value) {
  return /^[1-9]\d*$/.test(String(value)) && Number.isSafeInteger(Number(value)) ? Number(value) : null;
}

function transferSessionKey(user) {
  return JSON.stringify([user.id, user.active_business_unit_id, user.permissions]);
}

function normalizeStatus(value) {
  return String(value || "pending_approval").trim().toLowerCase();
}

function getStatusMeta(value) {
  const key = normalizeStatus(value);
  return TRANSFER_STATUS_META[key] || { label: key.replace(/_/g, " "), tone: "approved" };
}

function itemLabel(item) {
  if (!item) return "-";
  const code = item.code ? `${item.code} | ` : "";
  return `${code}${item.name || `Item ${item.id}`}`;
}

function itemHierarchyLabel(item) {
  return [item?.family_name, item?.subfamily_name, item?.category_name, item?.subcategory_name]
    .filter(Boolean)
    .join(" / ");
}

function itemSearchBlob(item) {
  return [item?.code, item?.name, itemHierarchyLabel(item)].filter(Boolean).join(" ").toLowerCase();
}

function locationSearchBlob(location) {
  return [location?.code, location?.name, location?.is_main ? "main" : ""]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function lineTotalValue(line) {
  const quantity = Number(line?.quantity || 0);
  const unitCost = Number(line?.unit_cost_snapshot || 0);
  return quantity * unitCost;
}

const roundQuantity = (value, digits = 8) => Number(value.toFixed(digits));
const baseUnit = (item) => item?.units?.find((unit) => unit.level === "unit");
const lineUnit = (line, item) => item?.units?.find((unit) => Number(unit.id) === Number(line?.unit_id)) || baseUnit(item);

function quantityInUnit(baseQuantity, unit, item, exact) {
  const basis = inventoryBasis(item);
  if (basis) return roundQuantity(Number(stockInUnit(baseQuantity, unit?.level || "unit", basis, exact, unit?.factor || 1)),
    unit && unit.level !== "unit" ? 12 : 8);
  const factor = Number(unit?.factor || 1);
  const quantity = Number(baseQuantity) * factor;
  const whole = Math.round(quantity);
  // A fraction stored to eight decimals can still represent a whole bottle.
  if (Number.isSafeInteger(whole) && roundQuantity(whole / factor) === roundQuantity(Number(baseQuantity))) return whole;
  return Number(quantity.toFixed(unit && unit.level !== "unit" ? 12 : 8));
}

// Every transfer has one source. A line can use all of that source's stock
// or request a quantity in its selected unit.
function canSupplyLine(line, item, stock) {
  if (!stock) return false;
  if (line.all_available) return true;
  const unit = lineUnit(line, item);
  const basis = inventoryBasis(item);
  if (basis) {
    const physical = line.exact_quantity || exactInput(line.quantity, unit?.level, basis);
    if (physical != null && stock.exact != null) return BigInt(physical.steps) > 0n &&
      BigInt(physical.steps) <= BigInt(stock.exact.steps);
  }
  const qty = Number(line.quantity);
  return Number.isFinite(qty) && qty === roundQuantity(qty, unit ? 12 : 8) &&
    roundQuantity(qty / (unit?.factor || 1)) > 0 && qty <= quantityInUnit(stock.base, unit, item, stock.exact);
}

function allStockQuantity(stock, unit, item) {
  return quantityFromExact(stock.exact, unit?.level) ?? String(quantityInUnit(stock.base, unit, item));
}

function transferDisplay(quantity, unit, languageId, item, exact) {
  const level = unit?.level || "unit";
  if (exact?.steps != null) return [formatExactQuantity(exact, level, "es"), getUnitName(unit, languageId)].filter(Boolean).join(" ");
  return formatStockTransferQuantity(displayQuantity(quantity, level, inventoryBasis(item)), unit, languageId);
}

function recordedQuantityLabel(line, languageId, item = line.item) {
  return transferDisplay(line.requested_unit?.quantity ?? line.quantity, line.requested_unit || baseUnit(item), languageId, item, line.exact_quantity);
}

function transferQuantitySummary(transfer, languageId) {
  const units = new Map();
  for (const line of transfer.lines || []) {
    const unit = line.requested_unit || baseUnit(line.item);
    const key = unit?.id ?? null;
    const sum = (units.get(key)?.sum || 0) + Number(line.requested_unit?.quantity ?? line.quantity);
    units.set(key, { sum, unit, configured: (units.get(key)?.configured ?? true) && Boolean(inventoryBasis(line.item)) });
  }
  return [...units.values()].map(({ sum, unit, configured }) =>
    formatStockTransferQuantity(configured ? Number(sum.toFixed(4)) : sum, unit, languageId)).join(" · ") || "0";
}

function transferTotalValue(transfer) {
  return (transfer.lines || []).reduce((sum, line) => sum + lineTotalValue(line), 0);
}

function transferSearchBlob(transfer) {
  return [
    transfer.transfer_code,
    transfer.status,
    transfer.notes,
    transfer.source_location?.name,
    transfer.source_location?.code,
    transfer.destination_location?.name,
    transfer.destination_location?.code,
    transfer.requested_by_user?.label,
    transfer.approved_by_user?.label,
    ...(transfer.lines || []).flatMap((line) => [
      line.item?.code,
      line.item?.name,
      line.item?.family_name,
      line.item?.category_name,
    ]),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function buildTransferRow(transfer, languageId) {
  const statusMeta = getStatusMeta(transfer.status);
  return {
    ...transfer,
    statusLabel: statusMeta.label,
    statusTone: statusMeta.tone,
    totalQty: transferQuantitySummary(transfer, languageId),
    totalValue: transferTotalValue(transfer),
    lineCount: Array.isArray(transfer.lines) ? transfer.lines.length : 0,
    searchBlob: transferSearchBlob(transfer),
  };
}

function TransferLinesSummary({ lines, t, languageId }) {
  const visibleLines = (lines || []).slice(0, 3);
  if (!visibleLines.length) return <span className="so-muted">{t("No lines")}</span>;

  return (
    <span className="so-muted">
      {visibleLines
        .map((line) => `${itemLabel(line.item)} (${recordedQuantityLabel(line, languageId)})`)
        .join(", ")}
      {(lines || []).length > visibleLines.length ? ` +${(lines || []).length - visibleLines.length}` : ""}
    </span>
  );
}

export default function StockTransfersWorkspace({ businessUnitId, pendingApprovalsOnly = false }) {
  const { languageId, themeId } = usePmsTheme();
  const locationLabel = (location) => location ? getLocationName(location, languageId) : "-";
  const dialog = useDocumentDialog();
  const decisionDialog = useStockApprovalDecision();
  const t = stockTransferText(languageId);
  const isMobile = useIsMobile();
  const [, refreshSession] = useState(0);
  const currentUser = readCurrentUser();
  const sessionKey = transferSessionKey(currentUser);
  const sessionKeyRef = useRef(sessionKey);
  sessionKeyRef.current = sessionKey;
  const userId = positiveSessionId(currentUser.id);
  const activeBusinessUnitId = positiveSessionId(currentUser.active_business_unit_id);
  const validSessionScope = Boolean(userId && activeBusinessUnitId && activeBusinessUnitId === positiveSessionId(businessUnitId));
  const canViewTransfers = validSessionScope && hasTransferRolePermission(currentUser);
  const canCreateTransfers = validSessionScope && hasTransferRolePermission(currentUser, "request");
  const requestScopeKey = `${sessionKey}:${businessUnitId}`;
  const loadedScopeRef = useRef(null);
  const contextIsCurrent = loadedScopeRef.current === requestScopeKey;
  const isCurrentSession = useCallback(() => validSessionScope && transferSessionKey(readCurrentUser()) === sessionKey,
    [validSessionScope, sessionKey]);
  const canUseTransferContext = (action) => contextIsCurrent && isCurrentSession() && hasTransferRolePermission(readCurrentUser(), action);
  const requestOptions = useMemo(() => ({ params: { business_unit_id: businessUnitId },
    headers: { "X-Pilcore-Business-Unit-Id": String(activeBusinessUnitId) }, pilcoreExpectedUserId: userId }),
  [businessUnitId, activeBusinessUnitId, userId]);
  const [loading, setLoading] = useState(true);
  const [context, setContext] = useState(null);
  const [transfers, setTransfers] = useState([]);
  const [activeView, setActiveView] = useState(pendingApprovalsOnly ? "pending" : "all");
  const [onlyMine, setOnlyMine] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [detailId, setDetailId] = useState(null);
  const [detailDocument, setDetailDocument] = useState(null);
  const [detailError, setDetailError] = useState(false);
  const [editingTransfer, setEditingTransfer] = useState(null);
  const deepLinkRef = useRef(null);
  const stepHeadingRef = useRef(null);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [busyAction, setBusyAction] = useState(null);
  const requestRef = useRef(0);
  const scopeRef = useRef(businessUnitId);
  const actionRef = useRef(false);
  const scopeEpochRef = useRef(0);
  scopeRef.current = businessUnitId;
  const [locationPicker, setLocationPicker] = useState(null);
  const [locationPickerSearch, setLocationPickerSearch] = useState("");
  const [itemSearch, setItemSearch] = useState("");
  const [groupFilters, setGroupFilters] = useState({});
  const [groupPicker, setGroupPicker] = useState(null);
  const [groupSearch, setGroupSearch] = useState("");
  const [form, setForm] = useState({
    source_location_id: "",
    destination_location_id: "",
    notes: "",
    lines: [],
  });

  useEffect(() => {
    const refresh = (event) => {
      if (event.type === "storage" && event.key && event.key !== "palantir_user") return;
      const nextSessionKey = transferSessionKey(readCurrentUser());
      if (nextSessionKey === sessionKeyRef.current) return;
      sessionKeyRef.current = nextSessionKey;
      requestRef.current += 1;
      scopeEpochRef.current += 1;
      refreshSession((revision) => revision + 1);
    };
    window.addEventListener("palantir-session-updated", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("palantir-session-updated", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  const loadAll = useCallback(async () => {
    const request = ++requestRef.current;
    if (!canViewTransfers || !isCurrentSession()) { setLoading(false); return; }

    setLoading(true);
    setLoadError(false);
    try {
      const [contextRes, transfersRes] = await Promise.all([
        axios.get("/api/purchasing/stock-transfers/context", requestOptions),
        axios.get("/api/purchasing/stock-transfers", requestOptions),
      ]);
      if (!isCurrentSession() || request !== requestRef.current || businessUnitId !== scopeRef.current) return;
      if (!contextRes.data || !Array.isArray(transfersRes.data)) throw new Error("Invalid stock transfer response");
      const nextContext = contextRes.data || {};
      loadedScopeRef.current = requestScopeKey;
      setContext(nextContext);
      setTransfers(Array.isArray(transfersRes.data) ? transfersRes.data : []);
    } catch (error) {
      if (!isCurrentSession() || request !== requestRef.current || businessUnitId !== scopeRef.current) return;
      console.error("[STOCK-TRANSFERS][LOAD][ERROR]", error);
      setContext(null);
      setTransfers([]);
      setLoadError(true);
    } finally {
      if (isCurrentSession() && request === requestRef.current && businessUnitId === scopeRef.current) setLoading(false);
    }
  }, [businessUnitId, canViewTransfers, requestOptions, requestScopeKey, isCurrentSession]);

  useEffect(() => {
    loadedScopeRef.current = null;
    setContext(null); setTransfers([]); setFeedback(null);
    setWizardOpen(false); setDetailId(null); setDetailDocument(null); setEditingTransfer(null); setActiveView(pendingApprovalsOnly ? "pending" : "all"); setSearch(""); setOnlyMine(false);
    scopeEpochRef.current += 1; actionRef.current = false; setSaving(false); setBusyAction(null);
    setLocationPicker(null); setGroupPicker(null);
    setForm({ source_location_id: "", destination_location_id: "", notes: "", lines: [] });
    loadAll();
    return () => { requestRef.current += 1; scopeEpochRef.current += 1; };
  }, [loadAll, pendingApprovalsOnly]);

  useEffect(() => {
    if (wizardOpen) stepHeadingRef.current?.focus();
  }, [wizardOpen, wizardStep]);

  useEffect(() => {
    let active = true;
    setDetailDocument(null); setDetailError(false);
    if (detailId && canViewTransfers && isCurrentSession()) {
      axios.get(`/api/purchasing/stock-transfers/${detailId}`, requestOptions)
        .then(({ data }) => { if (active && isCurrentSession()) setDetailDocument(data); })
        .catch(() => { if (active && isCurrentSession()) setDetailError(true); });
    }
    return () => { active = false; };
  }, [detailId, canViewTransfers, requestOptions, isCurrentSession]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = Number(params.get("document_id"));
    const key = `${businessUnitId}:${id}`;
    if (!loading && context && id > 0 && (!params.get("business_unit_id") || String(businessUnitId) === params.get("business_unit_id")) && deepLinkRef.current !== key) {
      deepLinkRef.current = key;
      setDetailId(id);
    }
  }, [loading, context, businessUnitId]);

  const sourceLocations = useMemo(() => context?.source_locations || [], [context?.source_locations]);
  const destinationLocations = useMemo(
    () => context?.destination_locations || [],
    [context?.destination_locations]
  );
  const items = useMemo(() => context?.items || [], [context?.items]);
  const balances = useMemo(() => context?.balances || {}, [context?.balances]);

  const itemById = useMemo(() => {
    const map = new Map();
    items.forEach((item) => map.set(Number(item.id), item));
    return map;
  }, [items]);

  const requestRoutes = useMemo(() => {
    if (!contextIsCurrent || !canCreateTransfers || context?.permissions?.request !== true) return [];
    return sourceLocations.map((source) => ({
      source,
      destinations: destinationLocations.filter((destination) => Number(source.id) !== Number(destination.id) &&
        source.permissions_by_destination?.[destination.id]?.request === true),
    })).filter((route) => route.destinations.length);
  }, [contextIsCurrent, canCreateTransfers, context?.permissions?.request, sourceLocations, destinationLocations]);

  const stockByItem = useMemo(() => {
    const catalog = new Map();
    for (const item of items) {
      const bySource = new Map();
      let maximum = null;
      const basis = inventoryBasis(item);
      for (const { source, destinations } of requestRoutes) {
        const base = balances[source.id]?.[item.id] ?? 0;
        const snapshot = context.exact_balances?.[source.id]?.[item.id];
        const hasSnapshotSteps = typeof snapshot?.steps === "string" && /^-?\d+$/.test(snapshot.steps);
        if (!(Number(base) > 0) && !(hasSnapshotSteps && BigInt(snapshot.steps) > 0n)) continue;
        // Match the ledger: an existing snapshot counts physical units even
        // when today's pack size differs from the historical one.
        let exact = null;
        if (basis) {
          exact = snapshot == null ? historicalExact(base, basis)
            : hasSnapshotSteps && Number(snapshot.unit_id) === Number(basis.unit_id)
              ? { ...basis, steps: snapshot.steps } : null;
        }
        if (exact ? BigInt(exact.steps) <= 0n : !(Number(base) > 0)) continue;
        const allowed = destinations.filter((dest) => item.permissions_by_route?.[`${source.id}:${dest.id}`]?.request === true);
        if (!allowed.length) continue;
        const stock = { base, exact, destinationIds: new Set(allowed.map((dest) => dest.id)) };
        bySource.set(source.id, stock);
        const larger = exact && maximum?.exact ? BigInt(exact.steps) > BigInt(maximum.exact.steps) : Number(base) > Number(maximum?.base || 0);
        if (!maximum || larger) maximum = stock;
      }
      if (maximum) catalog.set(Number(item.id), { bySource, maximum });
    }
    return catalog;
  }, [items, requestRoutes, balances, context]);

  const lineByItem = useMemo(() => new Map(form.lines.map((line) => [Number(line.item_id), line])), [form.lines]);
  const catalogItems = useMemo(() => {
    const stocked = items.filter((item) => stockByItem.has(Number(item.id)));
    // A returned request may contain a line that has since run out. Keep it
    // visible so its requester can remove it and correct the request.
    const unavailable = form.lines.filter((line) => !stockByItem.has(Number(line.item_id)))
      .map((line) => itemById.get(Number(line.item_id)) || line.item || { id: line.item_id });
    return [...stocked, ...unavailable];
  }, [items, stockByItem, form.lines, itemById]);

  const eligibleRoutes = useMemo(() => {
    if (!form.lines.length) return [];
    return requestRoutes.flatMap(({ source, destinations }) => {
      const stocks = form.lines.map((line) => stockByItem.get(Number(line.item_id))?.bySource.get(source.id));
      if (!form.lines.every((line, i) => canSupplyLine(line, itemById.get(Number(line.item_id)), stocks[i]))) return [];
      const common = destinations.filter((destination) => stocks.every((stock) => stock.destinationIds.has(destination.id)));
      return common.length ? [{ source, destinations: common }] : [];
    });
  }, [requestRoutes, form.lines, stockByItem, itemById]);
  const selectedRoute = eligibleRoutes.find(({ source }) => Number(source.id) === Number(form.source_location_id));
  const selectedSource = selectedRoute?.source;
  const selectedDestination = selectedRoute?.destinations.find((dest) => Number(dest.id) === Number(form.destination_location_id));
  const locationQuery = locationPickerSearch.trim().toLowerCase();
  const locationPickerOptions = (locationPicker === "source" ? eligibleRoutes.map(({ source }) => source) : selectedRoute?.destinations || [])
    .filter((location) => locationSearchBlob(location).includes(locationQuery));

  const itemQuery = itemSearch.trim().toLowerCase();
  const visibleItems = catalogItems.filter((item) => (!itemQuery || itemSearchBlob(item).includes(itemQuery)) &&
    TRANSFER_ITEM_GROUPS.every(({ key }) => !groupFilters[key] || itemGroup(item, key)?.key === groupFilters[key].key));

  const pickerGroups = useMemo(() => {
    if (!groupPicker) return [];
    const groups = new Map();
    const ancestors = TRANSFER_ITEM_GROUPS.slice(0, TRANSFER_ITEM_GROUPS.indexOf(groupPicker));
    catalogItems.forEach((item) => {
      if (ancestors.some(({ key }) => groupFilters[key] && itemGroup(item, key)?.key !== groupFilters[key].key)) return;
      const group = itemGroup(item, groupPicker.key);
      if (!group) return;
      if (!groups.has(group.key)) groups.set(group.key, { ...group, count: 0 });
      groups.get(group.key).count += 1;
    });
    return [...groups.values()].sort((a, b) => a.path.localeCompare(b.path, languageId || "en"));
  }, [catalogItems, groupPicker, groupFilters, languageId]);

  const itemCountLabel = (count) => count === 1 ? t("1 item") : t("{count} items", { count });
  const allFormLinesUseAvailable = form.lines.length > 0 && form.lines.every((line) => line.all_available);
  const linesValid = eligibleRoutes.length > 0;
  const routeValid = Boolean(selectedSource && selectedDestination);
  const selectedStock = (line) => stockByItem.get(Number(line.item_id))?.bySource.get(selectedSource?.id);
  const resolvedLines = form.lines.map((line) => {
    const stock = selectedStock(line);
    if (!line.all_available || !stock) return line;
    const item = itemById.get(Number(line.item_id));
    return { ...line, exact_quantity: stock.exact, quantity: allStockQuantity(stock, lineUnit(line, item), item) };
  });

  const applyRoleCapabilities = useCallback((row) => ({
    ...row,
    ...Object.fromEntries(["approve", "dispatch", "reject", "return", "cancel", "edit", "resubmit"].map((action) =>
      [`can_${action}`, contextIsCurrent && row[`can_${action}`] === true && hasTransferRolePermission(readCurrentUser(), action) &&
        (!["approve", "dispatch", "reject", "return"].includes(action) || context?.approval_dispatch_supported === true)])),
  }), [sessionKey, contextIsCurrent, context?.approval_dispatch_supported]);
  const transferRows = useMemo(() => (contextIsCurrent ? transfers : []).map((transfer) => {
    const row = applyRoleCapabilities(buildTransferRow(transfer, languageId));
    return { ...row, status: normalizeStatus(row.status), statusLabel: t(row.statusLabel), searchBlob: `${row.searchBlob} ${t(row.statusLabel).toLowerCase()}` };
  }), [contextIsCurrent, transfers, t, languageId, applyRoleCapabilities]);
  const query = search.trim().toLowerCase();

  const matchesView = (row, view) => view === "all" ||
    (view === "pending" ? row.status === "pending_approval" && row.can_approve === true :
      view === "ready" ? row.status === "approved" : row.status === view);
  const scopedRows = transferRows.filter((row) => !onlyMine || Number(row.requested_by_user_id) === Number(currentUser?.id));
  const visibleRows = scopedRows.filter((row) => matchesView(row, activeView) && (!query || row.searchBlob.includes(query)));
  const viewOptions = [
    { value: "all", label: "Requests", tone: "all" },
    { value: "pending", label: "Pending Approval", tone: "confirmed" },
    { value: "returned", label: "Returned", tone: "partial" },
    { value: "ready", label: "Pending dispatch", tone: "approved" },
    { value: "completed", label: "Completed", tone: "dispatched" },
    { value: "rejected", label: "Rejected", tone: "cancelled" },
    { value: "cancelled", label: "Cancelled", tone: "cancelled" },
  ].map((option) => ({ ...option, count: scopedRows.filter((row) => matchesView(row, option.value)).length }));
  const detail = contextIsCurrent && detailDocument ? { ...applyRoleCapabilities(buildTransferRow(detailDocument, languageId)), statusLabel: t(getStatusMeta(detailDocument.status).label) } : null;
  const updateLine = (itemId, patch) => {
    setForm((prev) => ({ ...prev, lines: prev.lines.map((line) => Number(line.item_id) === Number(itemId) ? { ...line, ...patch } : line) }));
  };

  const toggleLine = (item, checked) => {
    setForm((prev) => ({
      ...prev,
      lines: checked ? [...prev.lines, { item_id: item.id, unit_id: baseUnit(item)?.id, quantity: "", all_available: false }]
        : prev.lines.filter((line) => Number(line.item_id) !== Number(item.id)),
    }));
  };

  const openLocationPicker = (kind) => {
    setLocationPickerSearch("");
    setLocationPicker(kind);
  };

  const chooseLocation = (kind, location) => {
    setForm((prev) => {
      const locationId = String(location.id);
      if (kind === "source") {
        return {
          ...prev,
          source_location_id: locationId,
          destination_location_id: eligibleRoutes.find(({ source }) => source.id === location.id)?.destinations
            .some((dest) => Number(dest.id) === Number(prev.destination_location_id)) ? prev.destination_location_id : "",
        };
      }
      return { ...prev, destination_location_id: locationId };
    });
    setLocationPicker(null);
  };

  const applyGroupFilter = (group) => {
    const index = TRANSFER_ITEM_GROUPS.indexOf(groupPicker);
    setGroupFilters((previous) => {
      const next = { ...previous };
      for (const { key } of TRANSFER_ITEM_GROUPS.slice(index)) delete next[key];
      if (group) next[groupPicker.key] = group;
      return next;
    });
    setGroupPicker(null);
  };

  const clearFilters = () => { setItemSearch(""); setGroupFilters({}); };

  const resetForm = () => {
    setEditingTransfer(null);
    clearFilters();
    setForm({ source_location_id: "", destination_location_id: "", notes: "", lines: [] });
  };

  const editTransfer = async (id) => {
    if (!canUseTransferContext("edit") || actionRef.current) return;
    const scopeEpoch = scopeEpochRef.current;
    actionRef.current = true; setBusyAction(id); setFeedback(null);
    try {
      const { data } = await axios.get(`/api/purchasing/stock-transfers/${id}`, requestOptions);
      if (!isCurrentSession() || scopeEpoch !== scopeEpochRef.current || businessUnitId !== scopeRef.current) return;
      if (data.can_edit !== true || data.status !== "returned") throw new Error("This transfer is no longer editable. Reopen it to see its current state.");
      clearFilters();
      setEditingTransfer(data);
      setForm({ source_location_id: data.source_location_id, destination_location_id: data.destination_location_id, notes: data.notes || "", lines: (data.lines || []).map((line) => {
        const current = itemById.get(Number(line.item_id))?.units?.find((unit) => unit.id === line.requested_unit?.id &&
          Number(unit.factor) === Number(line.requested_unit.factor));
        return { ...line, unit_id: current?.id, quantity: String(current ? line.requested_unit.quantity : line.quantity), all_available: false };
      }) });
      setDetailId(null); setWizardStep(0); setWizardOpen(true);
    } catch (error) {
      if (scopeEpoch === scopeEpochRef.current) setFeedback({ tone: "error", message: error.response?.data?.error || error.message });
    } finally {
      if (scopeEpoch === scopeEpochRef.current) { actionRef.current = false; setBusyAction(null); }
    }
  };

  const submitTransfer = async (resubmit = false) => {
    if (!canUseTransferContext("request") || (editingTransfer && editingTransfer.can_edit !== true) || saving || actionRef.current || context?.request_only_supported !== true || context?.approval_dispatch_supported !== true || context?.permissions?.request !== true) return;

    const payload = {
      source_location_id: Number(form.source_location_id || 0),
      destination_location_id: Number(form.destination_location_id || 0),
      notes: form.notes,
      ...(editingTransfer ? { approval_revision: Number(editingTransfer.approval_revision || 0) } : {}),
      lines: resolvedLines
        .map((line) => {
          const item = itemById.get(Number(line.item_id));
          const unit = lineUnit(line, item);
          const physical = line.exact_quantity || exactInput(line.quantity, unit?.level, inventoryBasis(item));
          const decimal = quantityFromExact(physical, unit?.level);
          const quantity = Number(decimal ?? line.quantity);
          return { item_id: Number(line.item_id), quantity: decimal != null && String(quantity) !== decimal ? decimal : quantity,
            ...(physical && { exact_quantity: physical }), all_available: Boolean(line.all_available),
            ...(unit && { unit_id: unit.id, unit_factor: Number(unit.factor), base_unit_id: baseUnit(item).id }),
            ...(editingTransfer ? { notes: line.notes || null } : {}) };
        })
        .filter((line) => line.item_id > 0 && (line.quantity > 0 || Number(line.exact_quantity?.steps) > 0 || line.all_available)),
    };

    if (!routeValid || !linesValid) {
      setFeedback({ tone: "error", message: "Select source, destination and at least one item quantity." });
      return;
    }

    const scopeEpoch = scopeEpochRef.current;
    actionRef.current = true;
    setSaving(true);
    setFeedback(null);
    try {
      if (!editingTransfer) {
        const { data: plan } = await axios.post("/api/purchasing/stock-transfers/creation-plan", payload, requestOptions);
        if (!isCurrentSession() || businessUnitId !== scopeRef.current || scopeEpoch !== scopeEpochRef.current) return;
        const expectedPlan = TRANSFER_CREATION_PLANS[plan?.final_status];
        const requestedIds = new Set(payload.lines.map((line) => line.item_id));
        if (!expectedPlan || !plan.actions?.every((action) => hasTransferRolePermission(readCurrentUser(), action)) || JSON.stringify(plan.actions) !== JSON.stringify(expectedPlan.actions)
          || !Array.isArray(plan.lines) || plan.lines.length !== requestedIds.size
          || new Set(plan.lines.map((line) => Number(line.item_id))).size !== requestedIds.size
          || plan.lines.some((line) => !requestedIds.has(Number(line.item_id)) || !Number.isFinite(Number(line.quantity)) || (Number(line.quantity) <= 0 && !(Number(line.exact_quantity?.steps) > 0)))) {
          throw new Error("The authorized steps could not be checked. Please try again.");
        }
        // Freeze the quantities shown in the notice, including "all remaining"
        // selections, so stock arriving while it is open cannot increase them.
        const confirmedLines = plan.lines.map((line) => {
          const requested = payload.lines.find((entry) => entry.item_id === Number(line.item_id));
          const unit = line.requested_unit;
          const quantity = Number(unit?.quantity ?? line.quantity);
          const exact = line.exact_quantity;
          const baseQuantity = Number(line.quantity);
          // Repeated bottle transfers can differ by one ledger tick as the case empties.
          const tolerance = 1e-8 + Number.EPSILON * Math.max(1, baseQuantity);
          if ((unit?.id ?? null) !== (requested.unit_id ?? null) ||
              (unit && (Number(unit.factor) !== requested.unit_factor || !Number.isFinite(quantity) || (quantity <= 0 && !(Number(exact?.steps) > 0)) ||
                Math.abs(roundQuantity(quantity / requested.unit_factor) - baseQuantity) > tolerance)) ||
              (requested.exact_quantity && (!exact || Number(exact.unit_id) !== Number(requested.exact_quantity.unit_id))) ||
              (!requested.all_available && (quantity !== Number(requested.quantity) ||
                (requested.exact_quantity && exact.steps !== requested.exact_quantity.steps)))) {
            throw new Error("The authorized steps could not be checked. Please try again.");
          }
          return { ...requested, quantity: unit?.quantity ?? line.quantity, ...(exact && { exact_quantity: exact }), all_available: false };
        });
        const accepted = await dialog.confirm(<>
          <p><strong>{locationLabel(selectedSource)} → {locationLabel(selectedDestination)}</strong></p>
          <p>{t(expectedPlan.message)}</p>
          <ul>{plan.lines.map((line) => <li key={line.item_id}>{itemLabel(itemById.get(Number(line.item_id)))}: {recordedQuantityLabel(line, languageId, itemById.get(Number(line.item_id)))}</li>)}</ul>
          <p>{t("Accept to carry out these steps.")}</p>
        </>, { tone: plan.final_status === "completed" ? "warning" : "success" });
        if (!accepted || !isCurrentSession() || businessUnitId !== scopeRef.current || scopeEpoch !== scopeEpochRef.current || !plan.actions.every((action) => hasTransferRolePermission(readCurrentUser(), action))) return;
        payload.process_authorized = true;
        payload.expected_actions = plan.actions;
        payload.lines = confirmedLines;
      }
      if (!canUseTransferContext("request") || scopeEpoch !== scopeEpochRef.current) return;
      let res = editingTransfer
        ? await axios.patch(`/api/purchasing/stock-transfers/${editingTransfer.id}`, payload, requestOptions)
        : await axios.post("/api/purchasing/stock-transfers", payload, requestOptions);
      if (!isCurrentSession() || businessUnitId !== scopeRef.current || scopeEpoch !== scopeEpochRef.current) return;
      if (editingTransfer) {
        setEditingTransfer(res.data);
        if (resubmit && canUseTransferContext("resubmit")) res = await axios.post(`/api/purchasing/stock-transfers/${editingTransfer.id}/resubmit`, { approval_revision: res.data.approval_revision }, requestOptions);
      }
      if (!isCurrentSession() || businessUnitId !== scopeRef.current || scopeEpoch !== scopeEpochRef.current) return;
      setTransfers((prev) => [res.data, ...prev.filter((row) => row.id !== res.data.id)]);
      setWizardOpen(false); setActiveView("all"); setSearch(""); setOnlyMine(false);
      resetForm();
      await loadAll();
      if (!isCurrentSession() || businessUnitId !== scopeRef.current || scopeEpoch !== scopeEpochRef.current) return;
      setFeedback({ tone: "success", message: editingTransfer ? (resubmit ? "Request resubmitted. No stock has been moved." : "Changes saved. No stock has been moved.") : res.data.status === "completed" ? "Stock transfer completed." : res.data.status === "approved" ? "Request created and approved. Dispatch is pending; no stock has been moved." : "Request created. Approval and dispatch are pending; no stock has been moved." });
    } catch (error) {
      console.error("[STOCK-TRANSFERS][CREATE][ERROR]", error);
      if (isCurrentSession() && businessUnitId === scopeRef.current && scopeEpoch === scopeEpochRef.current) {
        const code = error.response?.data?.code;
        const editBlocked = editingTransfer && [403, 409].includes(error.response?.status) && code !== "STOCK_TRANSFER_UNIT_CHANGED";
        let message = error.response?.data?.error || error.message || "Failed to create stock transfer.";
        if (editBlocked) message = "This transfer is no longer editable. Reopen it to see its current state.";
        else if (code === "STOCK_CREATION_PLAN_CHANGED") message = "Your permissions have changed. Review and confirm the authorized steps again.";
        else if (code === "STOCK_TRANSFER_UNIT_CHANGED") message = "The item's unit or pack size changed. Reload the page and review the quantity.";
        setFeedback({ tone: "error", message });
        if (editBlocked) setEditingTransfer((previous) => ({ ...previous, can_edit: false, can_resubmit: false }));
      }
    } finally {
      if (scopeEpoch === scopeEpochRef.current) { actionRef.current = false; setSaving(false); }
    }
  };

  const postTransferAction = async (id, action) => {
    if (!canUseTransferContext(action) || actionRef.current) return;
    const row = detail?.id === id ? detail : transferRows.find((entry) => entry.id === id);
    if (!row || row[`can_${action}`] !== true || !["approve", "dispatch", "return", "reject", "cancel", "resubmit"].includes(action)) return;
    const scopeEpoch = scopeEpochRef.current;
    actionRef.current = true; setBusyAction(id); setFeedback(null);
    try {
      let comments;
      if (["return", "reject"].includes(action)) {
        comments = await decisionDialog.ask(action, row.transfer_code);
        if (comments === null) return;
      } else {
        const prompt = action === "approve" ? "Approve this transfer request? Stock will move only when it is dispatched." :
          action === "dispatch" ? "Dispatch this transfer? Stock will move between locations." :
          action === "resubmit" ? "Resubmit this transfer request for approval?" : "Cancel this transfer request?";
        if (!await dialog.confirm(t(prompt) + " " + row.transfer_code, { tone: ["approve", "dispatch", "resubmit"].includes(action) ? "success" : "warning" })) return;
      }
      if (!isCurrentSession() || businessUnitId !== scopeRef.current || scopeEpoch !== scopeEpochRef.current || !hasTransferRolePermission(readCurrentUser(), action)) return;
      const res = await axios.post(`/api/purchasing/stock-transfers/${id}/${action}`, {
        approval_revision: Number(row.approval_revision || 0),
        ...(action === "return" ? { comments } : action === "reject" ? { reason: comments } : {}),
      }, requestOptions);
      if (!isCurrentSession() || businessUnitId !== scopeRef.current || scopeEpoch !== scopeEpochRef.current) return;
      setTransfers((prev) => prev.map((entry) => (entry.id === id ? res.data : entry)));
      if (detailId === id) setDetailDocument(res.data);
      await loadAll();
    } catch (error) {
      if (isCurrentSession() && businessUnitId === scopeRef.current && scopeEpoch === scopeEpochRef.current) setFeedback({ tone: "error", message: error.response?.data?.error || "The transfer could not be updated. Please try again." });
    } finally {
      if (scopeEpoch === scopeEpochRef.current) { actionRef.current = false; setBusyAction(null); }
    }
  };

  const renderTransferActions = (row) => (
    <div className="so-actions so-actions--cell">
      {row.can_approve ? (
        <>
          <button
            type="button"
            className="so-btn so-btn--success so-btn--compact"
            disabled={busyAction !== null || saving}
            onClick={() => postTransferAction(row.id, "approve")}
          >{t("Approve")}</button>
        </>
      ) : null}
      {row.can_edit === true && row.status === "returned" && <button type="button" className="so-btn so-btn--warning so-btn--compact" disabled={busyAction !== null || saving} onClick={() => editTransfer(row.id)}>{t("Edit")}</button>}
      {row.can_resubmit === true && <button type="button" className="so-btn so-btn--primary so-btn--compact" disabled={busyAction !== null || saving} onClick={() => postTransferAction(row.id, "resubmit")}>{t("Resubmit")}</button>}
      {row.can_return === true && <button type="button" className="so-btn so-btn--warning so-btn--compact" disabled={busyAction !== null || saving} onClick={() => postTransferAction(row.id, "return")}>{t("Return with Comments")}</button>}
      {row.can_dispatch === true && <button type="button" className="so-btn so-btn--success so-btn--compact" disabled={busyAction !== null || saving} onClick={() => postTransferAction(row.id, "dispatch")}>{t("Dispatch")}</button>}
      {row.can_reject ? (
        <>
          <button
            type="button"
            className="so-btn so-btn--danger so-btn--compact"
            disabled={busyAction !== null || saving}
            onClick={() => postTransferAction(row.id, "reject")}
          >{t("Reject")}</button>
        </>
      ) : null}
      {row.can_cancel ? (
        <button
          type="button"
          className="so-btn so-btn--danger so-btn--compact"
          disabled={busyAction !== null || saving}
          onClick={() => postTransferAction(row.id, "cancel")}
        >{t("Cancel")}</button>
      ) : null}
      {!row.can_approve && !row.can_dispatch && !row.can_reject && !row.can_cancel && !row.can_edit && !row.can_resubmit && !row.can_return ? <span className="so-muted">—</span> : null}
    </div>
  );

  const renderTransfersTable = (rows, emptyText) => {
    if (isMobile) {
      return (
        <div className="so-mobileList">
          {rows.length ? (
            rows.map((row) => (
              <article className="so-mobileCard" key={row.id}>
                <div className="so-mobileTop">
                  <div>
                    <button type="button" className="so-codeButton" onClick={() => setDetailId(row.id)}>{row.transfer_code}</button>
                    <div className="so-mobileTitle">
                      {locationLabel(row.source_location)} → {locationLabel(row.destination_location)}
                    </div>
                  </div>
                  <span className={`so-status so-status--${row.statusTone}`}>{row.statusLabel}</span>
                </div>
                <div className="so-mobileRows">
                  <div>
                    <div className="so-mobileFieldLabel">{t("Date")}</div>
                    <div className="so-mobileFieldValue">{formatStockOpsDate(row.requested_at)}</div>
                  </div>
                  <div>
                    <div className="so-mobileFieldLabel">{t("Requested By")}</div>
                    <div className="so-mobileFieldValue">{row.requested_by_user?.label || "-"}</div>
                  </div>
                  <div>
                    <div className="so-mobileFieldLabel">{t("Lines")}</div>
                    <div className="so-mobileFieldValue">{row.lineCount}</div>
                  </div>
                  <div>
                    <div className="so-mobileFieldLabel">{t("Quantity")}</div>
                    <div className="so-mobileFieldValue">{row.totalQty}</div>
                  </div>
                </div>
                <div className="so-mobileFieldLabel" style={{ marginTop: 10 }}>{t("Items")}</div>
                <div className="so-mobileFieldValue">
                  <TransferLinesSummary lines={row.lines} t={t} languageId={languageId} />
                </div>
                <div className="so-mobileActions">{renderTransferActions(row)}</div>
              </article>
            ))
          ) : (
            <div className="so-empty">{emptyText}</div>
          )}
        </div>
      );
    }

    return (
      <div className="so-tableWrap" tabIndex={0} aria-label={t("Stock Transfers")}>
        <table className="so-table so-table--history so-transferTable">
          <colgroup>
            <col style={{ width: "11%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "11%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "9%" }} />
            <col style={{ width: "4%" }} />
            <col style={{ width: "5%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "auto" }} />
            <col style={{ width: "10%" }} />
          </colgroup>
          <thead>
            <tr>
              <th>{t("Transfer")}</th>
              <th>{t("Date")}</th>
              <th>{t("Status")}</th>
              <th>{t("Source")}</th>
              <th>{t("Destination")}</th>
              <th>{t("Requested By")}</th>
              <th className="so-number">{t("Lines")}</th>
              <th className="so-number">{t("Qty")}</th>
              <th className="so-number">{t("Value")}</th>
              <th>{t("Items")}</th>
              <th className="so-colAction">{t("Action")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length ? (
              rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <button type="button" className="so-codeButton" onClick={() => setDetailId(row.id)}>{row.transfer_code}</button>
                  </td>
                  <td className="so-muted">{formatStockOpsDate(row.requested_at)}</td>
                  <td>
                    <span className={`so-status so-status--${row.statusTone}`}>{row.statusLabel}</span>
                  </td>
                  <td className="so-muted" title={locationLabel(row.source_location)}>
                    <span className="so-ellipsis">
                      {locationLabel(row.source_location)}
                    </span>
                  </td>
                  <td className="so-muted" title={locationLabel(row.destination_location)}>
                    <span className="so-ellipsis">
                      {locationLabel(row.destination_location)}
                    </span>
                  </td>
                  <td className="so-muted">{row.requested_by_user?.label || "-"}</td>
                  <td className="so-number">{row.lineCount}</td>
                  <td className="so-number so-strong so-transferQuantityTotal">{row.totalQty}</td>
                  <td className="so-number">{formatStockOpsCurrency(row.totalValue)}</td>
                  <td title={(row.lines || []).map((line) => itemLabel(line.item)).join(", ")}>
                    <TransferLinesSummary lines={row.lines} t={t} languageId={languageId} />
                  </td>
                  <td className="so-cellAction">{renderTransferActions(row)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="11" className="so-empty">
                  {emptyText}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const closeWizard = async () => {
    if (saving) return;
    const scope = scopeRef.current;
    if ((form.lines.length || form.notes || form.destination_location_id) &&
      !await dialog.confirm(t("Discard this unsaved request?"), { tone: "warning" })) return;
    if (scope !== scopeRef.current) return;
    setWizardOpen(false); resetForm(); setFeedback(null);
  };

  const stepLabels = ["Items and quantities", "Locations", "Review request"];
  const renderWizardContent = () => wizardStep === 1 ? (
    <>
      <div className="so-transferComposer">
        <div className="so-transferRoute">
          <div className="so-transferEndpoint">
            <span className="so-label">{t("Source location")}</span>
            <button
              type="button"
              className={`so-locationSelector ${selectedSource ? "so-locationSelector--selected" : ""}`}
              onClick={() => openLocationPicker("source")}
              aria-label={`${t("Source location")}: ${locationLabel(selectedSource)}`}
              aria-haspopup="dialog"
              disabled={saving}
            >
              <span className="so-locationSelectorValue">{locationLabel(selectedSource) === "-" ? t("Choose source") : locationLabel(selectedSource)}</span>
              <span className="so-locationSelectorMeta">
                {selectedSource?.code || t("{count} allowed locations", { count: eligibleRoutes.length })}
              </span>
              <span className="so-locationSelectorChevron" aria-hidden="true" />
            </button>
          </div>

          <div className="so-transferDirection" aria-label={t("Transfer direction")}>
            <span className="so-transferDirectionLine" />
          </div>

          <div className="so-transferEndpoint">
            <span className="so-label">{t("Destination location")}</span>
            <button
              type="button"
              className={`so-locationSelector ${selectedDestination ? "so-locationSelector--selected" : ""}`}
              onClick={() => openLocationPicker("destination")}
              aria-label={`${t("Destination location")}: ${locationLabel(selectedDestination)}`}
              aria-haspopup="dialog"
              disabled={saving || !selectedSource}
            >
              <span className="so-locationSelectorValue">
                {locationLabel(selectedDestination) === "-" ? t("Choose destination") : locationLabel(selectedDestination)}
              </span>
              <span className="so-locationSelectorMeta">
                {selectedDestination
                  ? selectedDestination.is_user_location === false
                    ? t("Approval required")
                    : selectedDestination.code || t("Your location")
                  : t("{count} active locations", { count: selectedRoute?.destinations.length || 0 })}
              </span>
              <span className="so-locationSelectorChevron" aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className="so-transferComposerFooter">
          <div className="so-field so-transferNotes">
            <label className="so-label" htmlFor="stock-transfer-notes">{t("Notes")}</label>
            <input
              id="stock-transfer-notes"
              className="so-input"
              value={form.notes}
              disabled={saving}
              onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
              placeholder={t("Optional note for this transfer")}
            />
          </div>
        </div>
      </div>

      {!editingTransfer && form.destination_location_id ? (
        <div className="so-feedback so-feedback--info">
          <span className="so-muted">{t("Before confirmation, we will check your permissions for these locations and items and show which steps will happen automatically.")}</span>
        </div>
      ) : null}

    </>
  ) : wizardStep === 0 ? (
    <>
      <div className="so-lineToolbar">
        <label className="so-searchField"><FiSearch aria-hidden="true" />
          <input type="search" className="so-search" value={itemSearch} onChange={(event) => setItemSearch(event.target.value)}
            placeholder={t("Search by item code, name, family, category...")} aria-label={t("Search by item code, name, family, category...")} />
        </label>
        <div className="so-transferItemFilters">
          {TRANSFER_ITEM_GROUPS.map((group) => <button key={group.key} type="button" className="so-btn so-btn--ghost"
            onClick={() => { setGroupSearch(""); setGroupPicker(group); }} aria-haspopup="dialog" aria-pressed={Boolean(groupFilters[group.key])}>
            {t(group.filter)}{groupFilters[group.key] && ` · ${groupFilters[group.key].name}`}
          </button>)}
          {(itemSearch || Object.keys(groupFilters).length > 0) &&
            <button type="button" className="so-btn" onClick={clearFilters}>{t("Clear filters")}</button>}
        </div>
      </div>
      <div className="so-lineToolbar">
        <span className="so-label">{t("Items to transfer")} <span className="so-muted">· {form.lines.length ? itemCountLabel(form.lines.length) : t("No items selected")}</span></span>
        <label className={`so-checkControl ${form.lines.length ? "" : "so-checkControl--disabled"}`}>
          <input
            type="checkbox"
            checked={allFormLinesUseAvailable}
            disabled={!form.lines.length || saving}
            onChange={(event) => {
              const checked = event.target.checked;
              setForm((prev) => ({ ...prev, lines: prev.lines.map((line) => ({ ...line, all_available: checked })) }));
            }}
          />
          <span>{t("All available stock")}</span>
        </label>
      </div>

      <p className="so-transferHint">{t("Each quantity must fit in one source location. All stock uses the balance of the source you choose.")}</p>
      {form.lines.length > 0 && !linesValid && form.lines.every((line) => line.all_available || Number(line.quantity) > 0) &&
        <p className="so-transferNotice" role="status">{t("No single source can supply these quantities on an authorized route. Adjust the items or quantities to continue.")}</p>}
      <div className="so-tableWrap so-transferCatalogWrap" tabIndex={0} aria-label={t("Items to transfer")}>
        <table className="so-table so-transferCatalog">
          <thead><tr>
            <th>{t("Select")}</th><th>{t("Item")}</th><th>{t("Hierarchy")}</th><th>{t("Unit of measure")}</th>
            <th className="so-number">{t("Maximum at one source")}</th><th className="so-number">{t("Transfer Qty")}</th><th>{t("All Stock")}</th>
          </tr></thead>
          <tbody>
            {visibleItems.map((item) => {
              const line = lineByItem.get(Number(item.id));
              const unit = lineUnit(line, item);
              const maximum = stockByItem.get(Number(item.id))?.maximum;
              const sourceQty = maximum ? quantityInUnit(maximum.base, unit, item, maximum.exact) : 0;
              const basis = inventoryBasis(item);
              const chosenStock = line && selectedStock(line);
              const quantity = line?.all_available
                ? (chosenStock ? displayQuantity(allStockQuantity(chosenStock, unit, item), unit?.level, basis, chosenStock.exact) : "")
                : line?.quantityTyped ? line.quantity : displayQuantity(line?.quantity, unit?.level, basis, line?.exact_quantity);

              return (
                <tr key={item.id} className={line ? "so-transferCatalogSelected" : undefined}>
                  <td className="so-catalogCheck"><input type="checkbox" checked={Boolean(line)} disabled={saving}
                    aria-label={t("Select {item}", { item: itemLabel(item) })}
                    onChange={(event) => toggleLine(item, event.target.checked)} /></td>
                  <td className="so-catalogItem"><span className="so-strong">{itemLabel(item)}</span></td>
                  <td className="so-catalogHierarchy so-muted">{truncateStockOpsText(itemHierarchyLabel(item), 80) || "—"}</td>
                  <td className="so-catalogUnit" data-label={t("Unit of measure")}>
                    {item.units?.length > 1 ? <select className="so-select" value={unit.id} disabled={saving || !line}
                      aria-label={t("Transfer unit for {item}", { item: itemLabel(item) })}
                      onChange={(event) => {
                        const next = item.units.find((option) => Number(option.id) === Number(event.target.value));
                        const physical = line.exact_quantity || exactInput(line.quantity, unit.level, basis);
                        const base = Number(line.quantity) / unit.factor;
                        updateLine(item.id, { unit_id: next.id, quantityTyped: false, exact_quantity: physical,
                          quantity: !line.quantity ? "" : quantityFromExact(physical, next.level) ?? String(quantityInUnit(base, next, item)) });
                      }}>
                      {item.units.map((option) => <option key={option.id} value={option.id}>{getUnitName(option, languageId) || `#${option.id}`}</option>)}
                    </select> : getUnitName(unit, languageId) || t("Base unit")}
                    {unit && unit.level !== "unit" && <small className="so-transferUnitFactor">
                      1 {getUnitName(baseUnit(item), languageId)} = {formatStockTransferQuantity(unit.factor, unit, languageId)}
                    </small>}
                  </td>
                  <td className="so-number so-catalogMaximum" data-label={t("Maximum at one source")}>{transferDisplay(sourceQty, unit, languageId, item, maximum?.exact)}
                  </td>
                  <td className="so-number so-catalogQty" data-label={t("Transfer Qty")}>
                    <input type="number" min="0" step="any"
                      className="so-input so-input--qty so-number" value={quantity ?? ""} max={sourceQty}
                      placeholder={line?.all_available && !chosenStock ? t("At source") : undefined}
                      disabled={saving || !line}
                      aria-label={t("Transfer quantity for {item}", { item: itemLabel(item) })}
                      onBlur={() => { if (line?.quantityTyped && line.exact_quantity) updateLine(item.id, { quantityTyped: false }); }}
                      onChange={(event) => updateLine(item.id, {
                        quantity: event.target.value, exact_quantity: exactInput(event.target.value, unit?.level, basis),
                        quantityTyped: true, all_available: false,
                      })}
                    />
                  </td>
                  <td className="so-catalogAll"><label className="so-checkControl so-checkControl--compact">
                    <input type="checkbox" checked={Boolean(line?.all_available)} disabled={saving || !line}
                      aria-label={t("All stock for {item}", { item: itemLabel(item) })}
                      onChange={(event) => updateLine(item.id, { all_available: event.target.checked })} />
                    <span>{t("All")}</span>
                  </label></td>
                </tr>
              );
            })}
            {!visibleItems.length && <tr><td colSpan="7" className="so-empty">{t("No items with available stock match these filters.")}</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  ) : (
    <div className="so-transferReview">
      <div className="so-transferReviewRoute"><div><small>{t("Source location")}</small><strong>{locationLabel(selectedSource)}</strong></div><FiArrowRight aria-hidden="true" /><div><small>{t("Destination location")}</small><strong>{locationLabel(selectedDestination)}</strong></div></div>
      {form.notes && <p><strong>{t("Notes")}:</strong> {form.notes}</p>}
      <div className="so-tableWrap"><table className="so-table so-transferReviewTable"><thead><tr><th>{t("Item")}</th><th className="so-number">{t("Available Source Qty")}</th><th className="so-number">{t("Transfer Qty")}</th></tr></thead><tbody>
        {resolvedLines.map((line) => {
          const item = itemById.get(Number(line.item_id));
          const unit = lineUnit(line, item);
          const stock = selectedStock(line);
          return <tr key={line.item_id}><td>{itemLabel(item)}</td>
            <td className="so-number">{stock ? transferDisplay(quantityInUnit(stock.base, unit, item, stock.exact), unit, languageId, item, stock.exact) : "—"}</td>
            <td className="so-number so-strong">{transferDisplay(line.quantity, unit, languageId, item, line.exact_quantity)}</td></tr>;
        })}
      </tbody></table></div>
      <p className="so-transferNotice">{t(editingTransfer ? "Changes saved. No stock has been moved." : "Before confirmation, we will check your permissions for these locations and items and show which steps will happen automatically.")}</p>
    </div>
  );

  const renderWizard = () => !wizardOpen ? null : (
    <StockOperationsDialog title={t(editingTransfer ? "Edit transfer" : "New Transfer")} onClose={closeWizard} busy={saving} className="so-modal--wide so-transferWizard">
      <header className="so-modalHeader"><div><span className="so-modalKicker">{t("Stock Transfers")}</span><h2 className="so-modalTitle">{t(editingTransfer ? "Edit transfer" : "New Transfer")}</h2></div><button type="button" className="so-btn" onClick={closeWizard} disabled={saving} aria-label={t("Close")}><FiX /></button></header>
      <ol className="so-transferSteps" aria-label={t("Request steps")}>{stepLabels.map((label, index) => <li key={label}><button type="button" aria-current={wizardStep === index ? "step" : undefined}
        disabled={saving || index > wizardStep} onClick={() => setWizardStep(index)}><span>{index < wizardStep ? <FiCheck /> : index + 1}</span>{t(label)}</button></li>)}</ol>
      <div className="so-modalBody"><h3 ref={stepHeadingRef} tabIndex={-1} className="so-transferStepTitle">{t(stepLabels[wizardStep])}</h3>
        {feedback?.tone === "error" && <div className="so-feedback so-feedback--error" role="alert">{t(feedback.message)}</div>}
        {editingTransfer && <StockReturnNotice document={editingTransfer} />}
        {renderWizardContent()}
      </div>
      <footer className="so-modalFooter"><button type="button" className="so-btn" onClick={closeWizard} disabled={saving}>{t("Cancel")}</button>
        {wizardStep > 0 && <button type="button" className="so-btn" onClick={() => setWizardStep((step) => step - 1)} disabled={saving}>{t("Back")}</button>}
        {wizardStep < 2 ? <button type="button" className="so-btn so-btn--primary" onClick={() => setWizardStep((step) => step + 1)} disabled={wizardStep === 0 ? !linesValid : !routeValid}>{t("Next")} <FiArrowRight /></button> :
          <><button type="button" className="so-btn so-btn--success" onClick={() => submitTransfer(false)} disabled={saving || !routeValid || !linesValid || (editingTransfer && editingTransfer.can_edit !== true)}>{t(saving ? "Saving..." : editingTransfer ? "Save changes" : "Create request")}</button>
          {editingTransfer?.can_resubmit === true && <button type="button" className="so-btn so-btn--primary" onClick={() => submitTransfer(true)} disabled={saving || !routeValid || !linesValid}>{t("Save & Resubmit")}</button>}</>}
      </footer>
    </StockOperationsDialog>
  );

  const renderDetail = () => !detailId ? null : !detail ? <StockOperationsDialog title={t("Stock Transfer")} onClose={() => setDetailId(null)}>
    <div className="so-modalBody"><p role={detailError ? "alert" : "status"}>{t(detailError ? "The transfer could not be loaded. Close and try again." : "Loading stock transfers...")}</p><button type="button" className="so-btn" onClick={() => setDetailId(null)}>{t("Close")}</button></div>
  </StockOperationsDialog> : (
    <StockOperationsDialog title={detail.transfer_code} onClose={() => setDetailId(null)} busy={busyAction !== null} className="so-modal--wide so-transferDetail">
      <header className="so-modalHeader"><div><span className="so-modalKicker">{t("Stock Transfer")}</span><h2 className="so-modalTitle">{detail.transfer_code}</h2></div><span className={`so-status so-status--${detail.statusTone}`}>{detail.statusLabel}</span><button type="button" className="so-btn" onClick={() => setDetailId(null)} disabled={busyAction !== null}>{t("Close")}</button></header>
      <div className="so-modalBody">
        <div className="so-transferReviewRoute"><div><small>{t("Source")}</small><strong>{locationLabel(detail.source_location)}</strong></div><FiArrowRight /><div><small>{t("Destination")}</small><strong>{locationLabel(detail.destination_location)}</strong></div></div>
        <p className="so-muted">{t("Requested By")}: {detail.requested_by_user?.label || "—"} · {formatStockOpsDate(detail.requested_at)}</p>
        <StockReturnNotice document={detail} />
        {detail.notes && <p>{detail.notes}</p>}
        {detail.rejection_reason && <p>{detail.rejection_reason}</p>}
        {detail.restricted_lines && <p className="so-transferNotice">{t("Only items you can access are shown. A user with full access must process this request.")}</p>}
        {feedback?.tone === "error" && <div className="so-feedback so-feedback--error" role="alert">{t(feedback.message)}</div>}
        <div className="so-tableWrap"><table className="so-table so-transferReviewTable"><thead><tr><th>{t("Item")}</th><th>{t("Hierarchy")}</th><th className="so-number">{t("Quantity")}</th></tr></thead><tbody>
          {(detail.lines || []).map((line, index) => <tr key={line.id || index}><td>{itemLabel(line.item)}</td><td>{itemHierarchyLabel(line.item) || "—"}</td><td className="so-number">{recordedQuantityLabel(line, languageId)}</td></tr>)}
        </tbody></table></div>
        <StockApprovalHistory history={detail.approval_history} restricted={detail.history_restricted} />
      </div>
      <footer className="so-modalFooter">{renderTransferActions(detail)}</footer>
    </StockOperationsDialog>
  );

  const renderLocationPicker = () => {
    if (!locationPicker) return null;

    const isSourcePicker = locationPicker === "source";
    const selectedLocationId = isSourcePicker ? form.source_location_id : form.destination_location_id;
    const title = isSourcePicker ? "Select source location" : "Select destination location";
    const subtitle = isSourcePicker
      ? "Choose one of the locations you can transfer stock from."
      : "Choose an authorized destination for these items.";

    return (
      <StockOperationsDialog title={t(title)} onClose={() => setLocationPicker(null)} className="so-modal--picker">
          <div className="so-modalHeader">
            <div>
              <div className="so-modalKicker">{t("Stock Transfer")}</div>
              <h3 className="so-modalTitle">{t(title)}</h3>
              <p className="so-modalSubtitle">{t(subtitle)}</p>
            </div>
            <button type="button" className="so-btn so-btn--ghost so-btn--compact" onClick={() => setLocationPicker(null)}>{t("Close")}</button>
          </div>

          <div className="so-modalBody">
            <input
              type="search"
              className="so-search so-pickerSearch"
              value={locationPickerSearch}
              onChange={(event) => setLocationPickerSearch(event.target.value)}
              placeholder={t("Search locations by code or name...")}
              aria-label={t("Search locations by code or name...")}
            />

            <div className="so-pickerList">
              {locationPickerOptions.length ? (
                locationPickerOptions.map((location) => {
                  const isSelected = Number(location.id) === Number(selectedLocationId);
                  const requiresApproval = !isSourcePicker;
                  return (
                    <button
                      key={location.id}
                      type="button"
                      className={`so-pickerOption ${isSelected ? "so-pickerOption--selected" : ""}`}
                      onClick={() => chooseLocation(locationPicker, location)}
                      aria-pressed={isSelected}
                    >
                      <span className="so-pickerOptionMain">
                        <span className="so-pickerOptionTitle">{locationLabel(location)}</span>
                        {location.code && <span className="so-pickerOptionMeta">{location.code}</span>}
                      </span>
                      <span className="so-pickerOptionBadges">
                        {location.is_main ? <span className="so-pickerBadge">{t("Main")}</span> : null}
                        {!isSourcePicker && location.is_user_location ? <span className="so-pickerBadge">{t("Your location")}</span> : null}
                        {requiresApproval ? <span className="so-pickerBadge so-pickerBadge--warning">{t("Approval")}</span> : null}
                        {isSelected ? <span className="so-pickerBadge so-pickerBadge--selected">{t("Selected")}</span> : null}
                      </span>
                    </button>
                  );
                })
              ) : (
                <div className="so-empty">{t("No locations match this search.")}</div>
              )}
            </div>
          </div>
      </StockOperationsDialog>
    );
  };

  const renderGroupPicker = () => {
    if (!groupPicker) return null;
    const title = t(groupPicker.select);
    const searchLabel = t("Search classifications by name...");
    const query = groupSearch.trim().toLowerCase();
    const visibleGroups = pickerGroups.filter((group) => group.path.toLowerCase().includes(query));
    return (
      <StockOperationsDialog title={title} onClose={() => setGroupPicker(null)} className="so-modal--picker">
        <div className="so-modalHeader"><h3 className="so-modalTitle">{title}</h3>
          <button type="button" className="so-btn" onClick={() => setGroupPicker(null)}>{t("Close")}</button>
        </div>
        <div className="so-modalBody">
          <input type="search" className="so-search so-pickerSearch" value={groupSearch} onChange={(event) => setGroupSearch(event.target.value)}
            placeholder={searchLabel} aria-label={searchLabel} />
          <div className="so-pickerList">
            {groupFilters[groupPicker.key] && <button type="button" className="so-pickerOption"
              onClick={() => applyGroupFilter(null)}>{t("Clear this filter")}</button>}
            {visibleGroups.map((group) => <button type="button" key={group.key} className="so-pickerOption"
              aria-label={t("Select {group}", { group: group.path })} onClick={() => applyGroupFilter(group)}>
              <span className="so-pickerOptionMain"><span className="so-pickerOptionTitle">{group.name}</span>
                {group.parent && <span className="so-pickerOptionMeta">{group.parent}</span>}
              </span>
              <span className="so-pickerBadge">{itemCountLabel(group.count)}</span>
            </button>)}
            {!visibleGroups.length && <div className="so-empty">{t("No classifications with available stock match this search.")}</div>}
          </div>
        </div>
      </StockOperationsDialog>
    );
  };

  if (!canViewTransfers) return <div role="alert">{t("You do not have permission to view stock transfers.")}</div>;

  return (
    <>
      <section className="pilcore-purchasing-ui so-card so-transfers" data-theme={themeId} aria-label={t("Stock Transfers")} aria-busy={loading}>
        <header className="so-cardHead">
          <div className="so-cardHeadMain"><h2 className="so-cardTitle">{t("Stock Transfers")}</h2><p className="so-cardSubtitle">{t("Create transfers and confirm the steps you are authorized to perform. Any remaining steps stay pending.")}</p></div>
          {canCreateTransfers && <button type="button" className="so-btn so-btn--primary" disabled={loading || loadError || !requestRoutes.length || context?.permissions?.request !== true || context?.request_only_supported !== true || context?.approval_dispatch_supported !== true} aria-haspopup="dialog"
            onClick={() => { resetForm(); setWizardStep(0); setFeedback(null); setWizardOpen(true); }}><FiPlus />{t("New Transfer")}</button>}
        </header>
        {feedback && !wizardOpen && !detail && <div className={`so-feedback so-feedback--${feedback.tone}`} role={feedback.tone === "error" ? "alert" : "status"}>{t(feedback.message)}</div>}
        {!loading && !loadError && context && (context.request_only_supported !== true || context.approval_dispatch_supported !== true) && <div className="so-feedback" role="status">{t("The server must be updated to separate transfer approval from dispatch. Existing requests remain available.")}</div>}
        <div className="so-transferFilters" role="group" aria-label={t("Filter by status")}>
          {viewOptions.map((option) => <button type="button" key={option.value} aria-pressed={activeView === option.value} className={`so-transferFilter so-transferFilter--${option.tone}`} onClick={() => setActiveView(option.value)}>
            <span>{t(option.label)}</span><strong>{option.count}</strong>
          </button>)}
        </div>
        <div className="so-toolbar"><label className="so-searchField"><FiSearch aria-hidden="true" /><input type="search" className="so-search" value={search}
          onChange={(event) => setSearch(event.target.value)} placeholder={t("Search by transfer, location, item, requester or status...")} aria-label={t("Search transfers")} /></label>
          <label className="so-checkControl"><input type="checkbox" checked={onlyMine} onChange={(event) => setOnlyMine(event.target.checked)} />{t("Only my requests")}</label>
          <span className="so-muted">{t("{count} requests", { count: visibleRows.length })}</span>
        </div>
        {!businessUnitId ? <div className="so-empty">{t("Select a business unit to manage stock transfers.")}</div> : loading ? <div className="so-empty">{t("Loading stock transfers...")}</div> : loadError ? (
          <div className="so-feedback so-feedback--error" role="alert"><span>{t("Stock transfers could not be loaded. Please try again.")}</span><button type="button" className="so-btn" onClick={loadAll}>{t("Retry")}</button></div>
        ) : renderTransfersTable(visibleRows, t(activeView === "pending" && !onlyMine && !search ? "No transfers are waiting for your approval." : "No requests match these filters."))}
      </section>
      {renderDetail()}
      {contextIsCurrent && renderWizard()}
      {contextIsCurrent && renderLocationPicker()}
      {contextIsCurrent && renderGroupPicker()}
      {dialog.dialog}
      {decisionDialog.dialog}
    </>
  );
}
